import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Role, User } from 'src/app/models';
import { AccessControlService } from 'src/app/services/access-control.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { FormUtil } from 'src/app/utils/form.util';
import { MessageUtil } from 'src/app/utils/message.util';
import { StorageUtil } from 'src/app/utils/storage.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit {

  loading = false;
  changePassword = false;
  model: User = new User();
  origin: string;
  userForm: UntypedFormGroup;
  rolesList: { label: string; value: Role; }[];

  /**
   * EAS-327 — o backend está com "somente visualização" e capacidades por perfil? Sem isso (flag
   * desligada) a chave e o aviso não aparecem e nada é gravado no acesso: a tela fica como hoje.
   */
  viewOnlyAvailable = false;
  /** EAS-327 — somente visualização (grava acesso explícito VIEWER no api-security). */
  viewOnly = false;
  /** Valor com que a tela abriu: só salvamos o acesso se o toggle realmente mudou. */
  private viewOnlyOnLoad = false;
  /**
   * O acesso do usuário já chegou? Até lá a chave fica desabilitada: clicar antes perderia a confirmação das permissões
   * próprias (o perfil próprio ainda não é conhecido) e o valor de partida.
   */
  viewOnlyLoaded = false;

  /**
   * EAS-327 — o usuário tem permissões próprias (registro explícito em `user_marina_access`) que
   * **ignoram o perfil**. Sai do cadeado legado ou do backfill da migration do EAS-198. Com o
   * cadeado fora da listagem, esta é a única forma de a marina saber que isso existe.
   */
  hasOwnPermissions = false;
  /** Perfil do registro próprio do usuário nesta marina (ADMIN/VIEWER/CUSTOM), ou null quando segue o perfil. */
  private ownProfile: string = null;

  constructor(
    public dialogRef: DynamicDialogRef,
    private userService: UserService,
    private messageUtil: MessageUtil,
    private spinner: NgxSpinnerService,
    private toasty: ToastService,
    private config: DynamicDialogConfig,
    private accessService: AccessControlService
  ) { }

  ngOnInit(): void {
    this.resetForm();
    if (this.config.data.model) {
      // EBUG-426 — cópia, não o objeto da linha da lista: o save() grava nele o que foi digitado, e
      // uma edição recusada (troca de senha de conta usada em outra marina) deixava a senha na
      // linha. Reabrir a mesma conta sem recarregar mandava a senha de novo e a troca só do perfil
      // voltava barrada. O save() só troca campos de primeiro nível, então a cópia rasa basta.
      this.model = { ...this.config.data.model };
      this.origin = this.config.data.origin;
      this.userForm.patchValue(this.model);
      this.changePass();
    }
    this.findRoles();
    this.accessService.roleCapabilitiesEnabled().subscribe((enabled) => {
      this.viewOnlyAvailable = enabled;
      if (enabled) {
        this.loadViewOnly();
      }
    });
  }

  /** EAS-327 — o toggle reflete o acesso explícito VIEWER do usuário nesta marina. */
  private loadViewOnly(): void {
    if (!this.model || !this.model.id) {
      this.viewOnlyLoaded = true; // cadastro novo: não há acesso a carregar
      return;
    }
    this.accessService.getUserAccess(this.model.id, StorageUtil.getMarinaId()).subscribe({
      next: (access) => {
        // `explicit` separa permissão própria de valor derivado: sem ele, quem não tem nada
        // configurado também chega como ADMIN e a tela acusava permissão própria em todo usuário.
        const own = !!access && access.explicit === true;
        this.viewOnly = own && access.profile === 'VIEWER';
        this.viewOnlyOnLoad = this.viewOnly;
        this.hasOwnPermissions = own && access.profile !== 'VIEWER';
        this.ownProfile = own ? access.profile : null;
        this.viewOnlyLoaded = true;
      },
      error: () => {
        // sem acesso explícito legível: mantém desligado
        this.viewOnlyLoaded = true;
      }
    });
  }

  /**
   * EAS-327 — devolve o usuário ao recorte do perfil, apagando a permissão própria dele.
   * É o antídoto para o backfill do EAS-198: sem isto, configurar o perfil não tem efeito sobre
   * usuário antigo e ninguém consegue enxergar o motivo.
   */
  useRolePermissions(): void {
    Swal.fire({
      title: 'Usar as permissões do perfil?',
      text: 'As permissões próprias deste usuário serão removidas e ele passa a seguir exatamente o '
        + 'perfil atribuído. Passa a valer no próximo acesso dele.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (!result.value) {
        return;
      }
      this.spinner.show();
      this.accessService.clearUserAccess(this.model.id, StorageUtil.getMarinaId()).subscribe({
        next: () => {
          this.spinner.hide();
          this.hasOwnPermissions = false;
          this.ownProfile = null;
          this.viewOnly = false;
          this.viewOnlyOnLoad = false;
          this.toasty.success('Usuário passou a seguir as permissões do perfil.');
        },
        error: () => {
          this.spinner.hide();
          Swal.fire('Atenção', 'Não foi possível remover as permissões próprias.', 'warning');
        }
      });
    });
  }

  /**
   * EAS-327 — só o **perfil fixo Administrador** recusa somente visualização (CAM-03).
   *
   * Identificado pelo `code`, não pela flag `administrator` do perfil: essa flag significa outra
   * coisa ("pode cancelar pagamento, boleto, fatura e aplicar desconto" — ver o tooltip na tela do
   * perfil) e vem **ligada por padrão**, então usá-la travava a chave em praticamente todo perfil
   * da marina.
   */
  get isAdminRole(): boolean {
    const role: Role = this.userForm.get('roleId').value;
    return !!role && role.systemDefault === true && role.code === 'ADMINISTRADOR';
  }

  /**
   * Administrador não pode **ganhar** somente visualização (CAM-03), mas se já estiver com ele
   * ligado — estado legado possível: perfil Administrador + registro explícito VIEWER — tem que dar
   * para desligar. Travar o switch nos dois sentidos deixaria o usuário preso em somente
   * visualização sem saída pela tela.
   */
  get viewOnlyLocked(): boolean {
    return this.isAdminRole && !this.viewOnly;
  }

  /** A chave está desabilitada: perfil fixo Administrador (ver {@link viewOnlyLocked}) ou acesso ainda carregando. */
  get viewOnlyDisabled(): boolean {
    return this.viewOnlyLocked || !this.viewOnlyLoaded;
  }

  /**
   * Revisão EAS-327 — ligar o "somente visualização" grava VIEWER no lugar do registro próprio: as permissões de ação
   * configuradas individualmente (CUSTOM) se perdem, e ao desligar depois o usuário passa a seguir o perfil (que, sem
   * recorte configurado, é acesso total). Isso pede confirmação; cancelar deixa a chave desligada.
   */
  onViewOnlyChange(checked: boolean): void {
    if (!checked || this.ownProfile !== 'CUSTOM' || this.viewOnlyOnLoad) {
      return;
    }
    Swal.fire({
      title: 'Ligar o somente visualização?',
      text: 'Este usuário tem permissões de ação próprias, configuradas individualmente. Ao ligar o somente '
        + 'visualização elas são apagadas; se a chave for desligada depois, ele passa a seguir as permissões do perfil.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ligar mesmo assim',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (!result.value) {
        this.viewOnly = false;
      }
    });
  }

  onRoleChange(): void {
    if (this.isAdminRole) {
      this.viewOnly = false;
    }
  }

  passwordMatch(c: AbstractControl): any {
    return c.get('password').value === c.get('confirmPassword').value ? null : { nomatch: true };
  }

  usernameInUse(): void {
    // EBUG-426 — na edição o username é somente leitura: a busca acharia o próprio usuário, e o
    // reset apagava o login do formulário.
    if (this.model && this.model.id) {
      return;
    }
    const typed = (this.userForm.get('username').value || '').trim();
    if (!typed) {
      return;
    }
    this.userService.getByUsername(typed)
      .subscribe(
        (data) => {
          // Compara o username devolvido: o backend antigo cortava o sufixo do path ("joao.silva"
          // chegava como "joao") e podia achar outra conta. O vínculo de conta existente no
          // cadastro está desligado no backend, então o login que já existe segue barrado aqui.
          if (data && data.username && data.username.toLowerCase() === typed.toLowerCase()) {
            Swal.fire('Alerta!', `Usuário ${typed} já cadastrado`, 'warning');
            this.userForm.get('username').reset();
          }
        });
  }

  validPassword(): void {
    if (this.userForm.get('password').value !== this.userForm.get('confirmPassword').value) {
      Swal.fire('Alerta!', 'Senhas não conferem', 'warning');
      this.userForm.get('password').reset();
      this.userForm.get('confirmPassword').reset();
    }
  }

  formValidations(): boolean {
    if (this.userForm.valid) {
      return true;
    } else {
      FormUtil.touchAndSendFormGroup(this.userForm);
      Swal.fire('Alerta!', 'Por favor, preencha os campos obrigatórios', 'warning');
      return false;
    }
  }

  save(): void {
    this.loading = true;
    this.spinner.show();
    if (this.model && this.model.id) {
      Object.keys(this.userForm.controls).forEach((key) => {
        this.model[key] = this.userForm.get(key).value;
      });
      this.userService.update(this.model).subscribe(
        (data) => {
          this.saveViewOnly(this.model.id, () => {
            this.spinner.hide();
            this.toasty.success('Usuário alterado com sucesso!');
            this.dialogRef.close(data);
          });
        },
        (error) => {
          this.spinner.hide();
          const exception = error.error.data.exception;
          this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
        }
      );
    } else {
      this.model = this.userForm.value;
      this.model.marina = StorageUtil.getMarina();
      this.model.marinas = [];
      this.model.marinas.push(StorageUtil.getMarina());
      this.userService.create(this.model).subscribe(
        (data) => {
          // EBUG-426 — a resposta é o envelope {data: {user}}. Quando o login já tinha conta no
          // EasyMarine, a API vincula essa conta à marina (existingAccountLinked=true) sem aplicar o
          // nome, o e-mail e a senha digitados, e quem cadastrou precisa saber disso.
          const saved: any = data && (data as any).data && (data as any).data.user;
          // EAS-327: o id do usuário salvo (para o "somente visualização") vem de dentro do envelope.
          this.saveViewOnly(saved && saved.id, () => {
            this.spinner.hide();
            if (saved && saved.existingAccountLinked) {
              this.messageUtil.generateMessage('warning', 'SUMMARY.WARNING',
                'O usuário ' + saved.username + ' já tinha conta no EasyMarine e foi vinculado a esta marina. ' +
                'O nome, o e-mail e a senha digitados não foram aplicados: ele entra com a senha que já usa ' +
                'ou pelo "Esqueci minha senha".');
            } else {
              this.toasty.success('Usuário salvo com sucesso!');
            }
            this.dialogRef.close(data);
          });
        },
        (error) => {
          this.spinner.hide();
          const exception = error.error.data.exception;
          this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
        }
      );
    }
  }

  /**
   * EAS-327 — grava o somente visualização **só quando o toggle mudou** (e só com o EAS-327 ligado
   * no backend).
   *
   * Salvar sem mexer no toggle não pode encostar no acesso: usuário antigo pode ter registro
   * explícito (o backfill ADMIN da V1780491135 ou uma configuração legada do cadeado), e apagá-lo
   * de passagem mudaria a permissão dele sem ninguém pedir.
   */
  private saveViewOnly(userId: number, done: () => void): void {
    if (!this.viewOnlyAvailable || !this.viewOnlyLoaded || !userId || this.viewOnly === this.viewOnlyOnLoad) {
      done();
      return;
    }
    const marinaId = StorageUtil.getMarinaId();
    const request = this.viewOnly
      ? this.accessService.setViewOnly(userId, marinaId)
      : this.accessService.clearUserAccess(userId, marinaId);

    request.subscribe({
      next: () => done(),
      error: () => {
        this.spinner.hide();
        Swal.fire('Atenção',
          'O usuário foi salvo, mas não foi possível gravar o "somente visualização". Tente novamente pela edição do usuário.',
          'warning');
        this.dialogRef.close(this.model);
      }
    });
  }

  isGestor(): boolean {
    return this.model && this.model.marinas && this.model.marinas.length > 0;
  }

  resetForm(): void {
    this.userForm = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      username: new UntypedFormControl(null, Validators.required),
      password: new UntypedFormControl(null, Validators.required),
      confirmPassword: new UntypedFormControl(null, Validators.required),
      firstName: new UntypedFormControl(null, Validators.required),
      lastName: new UntypedFormControl(null, Validators.required),
      email: new UntypedFormControl(null, Validators.required),
      bracelet: new UntypedFormControl(null),
      changePassword: new UntypedFormControl(false),
      active: new UntypedFormControl(true),
      operator: new UntypedFormControl(false),
      roleId: new UntypedFormControl(null, Validators.required),
      // Permissão de token de integração da API pública. Começa desligada: é concessão explícita,
      // e o token que ela libera não expira.
      integrationTokenEnabled: new UntypedFormControl(false)
    });
  }

  changePass($event?: any): void {
    if ($event && $event.checked) {
      this.userForm.get('password').enable();
      this.userForm.get('confirmPassword').enable();
      this.userForm.get('password').reset();
      this.userForm.get('confirmPassword').reset();
    } else {
      // EBUG-426 — controle desabilitado mantém o valor, e o save() lê controle a controle: sem
      // limpar, desligar "Mudar senha" depois de digitar ainda mandava a senha (a conta desta
      // marina tinha a senha trocada; a compartilhada seguia barrada mesmo trocando só o perfil).
      this.userForm.get('password').setValue(null);
      this.userForm.get('confirmPassword').setValue(null);
      this.userForm.get('password').disable();
      this.userForm.get('confirmPassword').disable();
    }
  }

  /**
   * EAS-327 — o Visualizador saiu da lista de perfis oferecidos, e a lista vem filtrada do
   * backend pelo usuário **logado** — ela não sabe quem está sendo editado. Sem isto, abrir um
   * usuário que ainda está no Visualizador (ou em qualquer perfil não mais oferecido) mostraria o
   * campo vazio e salvar trocaria o perfil dele sem intenção (CAR-03/CAR-07).
   *
   * Também normaliza a seleção para a instância da lista: o `p-dropdown` casa por referência, e o
   * perfil que vem no usuário é outro objeto.
   */
  private keepCurrentRoleInList(): void {
    const current: Role = this.model && (this.model as any).roleId;
    if (!current || !current.id || !this.rolesList) {
      return;
    }
    const match = this.rolesList.find((opt) => opt.value && opt.value.id === current.id);
    if (match) {
      this.userForm.get('roleId').setValue(match.value);
      return;
    }
    this.rolesList = [...this.rolesList, { label: `${current.name} (perfil atual)`, value: current }];
    this.userForm.get('roleId').setValue(current);
  }

  async findRoles(): Promise<void> {
    return new Promise<void>(
      async (res) => {
        this.userService.getRolesByHierarchy().subscribe(
          (data) => {
            this.rolesList = data.map((m) => ({ label: m.name, value: m }));
            this.keepCurrentRoleInList();
            res();
          },
          (error) => { }

        );

      }
    );
  }

}

